<!--
@component
MatchesPage

Route page component.
-->

<script lang="ts">
  import { getHomeTeam, getAwayTeam } from '$utils/getMatchStats';
  import getMatchPath from '$utils/getMatchPath';
  import parseSeo from '$utils/parseSeo';
  import getTeams from '$utils/getTeams';
  import { getCommentary, getMatchStats } from '$utils/data';
  import getPath from '$utils/getPath';

  import Page from '$lib/components/MatchPage/index.svelte';
  // import SEO from '$lib/components/common/SEO/SEO.svelte';

  import { appState } from '$lib/stores/index.svelte';

  /* Matches data from API */
  let { data } = $props();

  const content = $derived(data.content);

  const teams = getTeams(appState.optaData.matches);

  let match = $state(appState.matches.getMatch(data.matchId));

  const homeTeam = $derived(getHomeTeam(match));
  const awayTeam = $derived(getAwayTeam(match));

  $effect(() => {
    appState.teams = teams;
  });

  let commentary = $state(data.commentary);
  let matchStats = $state(data.matchStats);

  const getLiveData = async () => {
    if (!match) return;

    match = appState.matches.getMatch(data.matchId);

    // Skip fetching live data for fixtures...
    if (match.liveData.matchDetails.matchStatus === 'Fixture') return;

    try {
      matchStats = await getMatchStats({ fetch, matchId: data.matchId });
    } catch (e) {}

    try {
      commentary = await getCommentary({ fetch, matchId: data.matchId });
    } catch (e) {}
  };

  // Poll for live updates while the page is mounted. We deliberately do NOT
  // fetch immediately on mount — the SvelteKit `+layout.js` load already
  // supplied the initial `matchStats` and `commentary`, so an immediate
  // re-fetch here would cause a visible flicker as the page renders twice.
  $effect(() => {
    // Don't bother polling for matches that haven't started yet.
    if (!match || match.liveData?.matchDetails?.matchStatus === 'Fixture') {
      return;
    }

    const pollingInterval = setInterval(() => {
      getLiveData();
    }, 5000);

    return () => {
      clearInterval(pollingInterval);
    };
  });

  const matchUrl = $derived(
    getMatchPath(data.match)
      ?.url.split('/')
      .filter((d: string) => d)
      .slice(-1)[0] ?? ''
  );

  const getTeamSlug = (name: string | undefined) =>
    name ? teams.getTeam(name)?.slug : undefined;

  const seo = $derived(
    parseSeo(content, 'match-seo', {
      homeTeam: homeTeam?.name,
      homeTeamSlug: getTeamSlug(homeTeam?.name),
      awayTeam: awayTeam?.name,
      awayTeamSlug: getTeamSlug(awayTeam?.name),
      matchUrl,
    })
  );
</script>

<!-- <SEO {seo} /> -->

<Page
  {commentary}
  {matchStats}
  data={appState.optaData}
  {match}
  {homeTeam}
  {awayTeam}
  {matchUrl}
  galleriesUrl={content['galleries-url']}
/>

<svelte:head>
  <link
    rel="preload"
    as="image"
    href={getPath('images/flags/rects/UNKNOWN.png')}
  />
  <link
    rel="preload"
    as="image"
    href={getPath('images/flags/circles/UNKNOWN.png')}
  />
  {#each teams as team}
    <link
      rel="preload"
      as="image"
      href={getPath(`images/flags/rects/${team.slug}.png`)}
    />
    <link
      rel="preload"
      as="image"
      href={getPath(`images/flags/circles/${team.slug}.png`)}
    />
  {/each}
</svelte:head>
